import { useWebSocket } from '@/hooks/useWebSocket'
import { useNotificationsStore } from '@/store/useNotificationsStore'
import { useRealtimeStore } from '@/store/useRealtimeStore'
import type { Conversation, ConversationMessage } from '@/types/conversation'

type NotificationEvent = {
  type: 'notification'
  payload: {
    id: string
    title: string
    message: string
    createdAt: string
    category?: string
  }
}

// AI Streaming events from backend
type AIStreamEvent = {
  type: 'ai_stream'
  sessionId: string
  chunk: string
  messageId: string
}

type AIStreamCompleteEvent = {
  type: 'ai_stream_complete'
  sessionId: string
  message: {
    id: string
    sessionId: string
    senderId: string
    senderType: string
    content: string
    timestamp: number
    confidence?: number
  }
}

type MessageEvent = {
  type: 'message'
  message: {
    id: string
    sessionId: string
    senderId: string
    senderType: string
    content: string
    timestamp: number
  }
}

type ConversationEvent =
  | { type: 'conversation.created'; payload: Conversation }
  | { type: 'conversation.updated'; payload: Conversation }
  | { type: 'conversation.closed'; payload: { id: string } }
  | { type: 'message.created'; payload: ConversationMessage }
  | { type: 'notification'; payload: NotificationEvent['payload'] }
  | { type: 'connected'; connectionId?: string }
  | { type: 'notification_connected'; connectionId?: string }
  | { type: 'session_status_changed'; sessionId?: string; status?: string; reason?: string }
  | { type: 'ai_transfer'; sessionId?: string; reason?: string }
  | AIStreamEvent
  | AIStreamCompleteEvent
  | MessageEvent

export const useRealtimeSubscriptions = () => {
  const { addNotification } = useNotificationsStore()
  const { updateConversation, addMessage, appendStreamChunk, removeConversation } = useRealtimeStore()

  useWebSocket<ConversationEvent>('/chat/admin/ws', {
    enabled: true,
    onMessage: (event) => {
      switch (event.type) {
        case 'conversation.created':
        case 'conversation.updated':
          updateConversation(event.payload)
          break
        case 'conversation.closed':
          removeConversation(event.payload.id)
          break
        case 'message.created':
          addMessage(event.payload.conversationId, event.payload)
          break
        case 'notification':
          if ('payload' in event && event.payload) {
            const payload = event.payload as NotificationEvent['payload']
            addNotification({
              id: payload.id,
              type: 'system',
              title: payload.title,
              message: payload.message,
              createdAt: payload.createdAt,
              read: false,
            })
          }
          break
        case 'connected':
        case 'notification_connected':
          // Connection acknowledgment - no action needed
          console.log('WebSocket connection acknowledged')
          break
        // AI Streaming events - real-time AI response streaming
        case 'ai_stream':
          if ('sessionId' in event && 'chunk' in event) {
            appendStreamChunk(event.sessionId, event.chunk, event.messageId)
          }
          break
        case 'ai_stream_complete':
          if ('message' in event && event.message) {
            const msg = event.message
            // Add the complete AI message to the conversation
            addMessage(msg.sessionId, {
              id: msg.id,
              conversationId: msg.sessionId,
              senderId: msg.senderId,
              senderType: msg.senderType as 'visitor' | 'agent' | 'system',
              body: msg.content,
              createdAt: new Date(msg.timestamp).toISOString(),
            })
          }
          break
        case 'message':
          // Direct message event from backend (AI or human)
          if ('message' in event && event.message) {
            const msg = event.message
            addMessage(msg.sessionId, {
              id: msg.id,
              conversationId: msg.sessionId,
              senderId: msg.senderId,
              senderType: msg.senderType as 'visitor' | 'agent' | 'system',
              body: msg.content,
              createdAt: new Date(msg.timestamp).toISOString(),
            })
          }
          break
        case 'session_status_changed':
        case 'ai_transfer': {
          // Transfer notification - add to notification bell only
          // The IncomingChatNotification modal is handled by useIncomingChatNotifications.ts
          // via the NotificationManager WebSocket (incoming_chat_notification events).
          // Do NOT create duplicate frontend-only notifications here.
          const sessionId = 'sessionId' in event ? event.sessionId : undefined
          const reason = 'reason' in event ? (event.reason as string) : 'Customer requested transfer'
          console.log('[Realtime] Transfer event:', event.type, sessionId)

          addNotification({
            id: `transfer-${sessionId || Date.now()}`,
            type: 'system',
            title: '🔔 New Transfer Request',
            message: reason || 'A customer has been transferred to an agent.',
            createdAt: new Date().toISOString(),
            read: false,
          })
          break
        }
        default:
          console.log('Unknown WebSocket event type:', event)
          break
      }
    },
    onOpen: () => {
      console.log('WebSocket connected to admin monitoring')
    },
    onError: (error) => {
      console.warn('WebSocket connection error (non-critical):', error)
    },
    onClose: (event) => {
      console.log('WebSocket disconnected, will attempt to reconnect...', event.code, event.reason)
    },
  })
}
